import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { showSuccessToast, showToast } from '../notifications/toast';
import { transferSelectionLabel } from './fileBrowserDestination';
import {
  parseItemPathForRoutes,
  toVirtualPath,
  type MountRoute
} from './createFileBrowserProvider';
import { resetTransferBatchState } from './fileConflictDialog';
import { performFileTransfer } from './fileBrowserTransfer';
import { isMountUnavailable, mountHealthMessage } from './fileBrowserMountAccess';

export const JARVIS_CUT = 'jarvisCut';
export const JARVIS_CLIPBOARD_COPY = 'jarvisClipboardCopy';
export const JARVIS_PASTE = 'jarvisPaste';

export type ClipboardOperation = 'copy' | 'cut';

export interface ClipboardEntry {
  mountId: string;
  mountKey: string;
  relativePath: string;
  name: string;
  isDirectory: boolean;
  virtualPath: string;
}

export interface FileBrowserClipboardState {
  operation: ClipboardOperation;
  items: ClipboardEntry[];
}

let clipboard: FileBrowserClipboardState | null = null;
const listeners = new Set<() => void>();

export function subscribeFileBrowserClipboard(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyClipboardChanged(): void {
  for (const listener of listeners) listener();
}

export function getClipboardState(): FileBrowserClipboardState | null {
  return clipboard;
}

export function hasClipboardItems(): boolean {
  return (clipboard?.items.length ?? 0) > 0;
}

export function clearClipboard(): void {
  if (!clipboard) return;
  clipboard = null;
  notifyClipboardChanged();
}

export function clipboardItemLabel(state: FileBrowserClipboardState | null | undefined): string {
  return transferSelectionLabel((state?.items ?? []).map((item) => item.name));
}

export function clipboardStatusLabel(state: FileBrowserClipboardState | null | undefined): string {
  if (!state?.items.length) return '';
  const verb = state.operation === 'cut' ? 'Cut' : 'Copied';
  return `${verb} ${clipboardItemLabel(state)}`;
}

export function clipboardPasteActionLabel(state: FileBrowserClipboardState | null | undefined): string {
  if (!state?.items.length) return 'Paste';
  return `Paste ${clipboardItemLabel(state)}`;
}

export function clipboardPasteHint(state: FileBrowserClipboardState | null | undefined): string {
  if (!state?.items.length) return 'Paste from clipboard';
  return `${clipboardPasteActionLabel(state)} into the current folder (Ctrl+V)`;
}

export function parentVirtualPath(virtualPath: string): string {
  const trimmed = virtualPath.replace(/\/+$/g, '');
  const slash = trimmed.lastIndexOf('/');
  return slash === -1 ? '' : trimmed.slice(0, slash);
}

export function clipboardCutNamesInDirectory(
  state: FileBrowserClipboardState | null | undefined,
  currentPath: string
): ReadonlySet<string> {
  if (state?.operation !== 'cut' || !state.items.length) return new Set();
  const current = (currentPath ?? '').replace(/\/+$/g, '');
  return new Set(
    state.items.filter((item) => parentVirtualPath(item.virtualPath) === current).map((item) => item.name)
  );
}

export function applyClipboardCutStyles(root: HTMLElement | null | undefined, currentPath: string): void {
  if (!root) return;
  for (const el of root.querySelectorAll('.jarvis-clipboard-cut')) {
    el.classList.remove('jarvis-clipboard-cut');
  }
  const names = clipboardCutNamesInDirectory(getClipboardState(), currentPath);
  if (names.size === 0) return;

  const mark = (container: HTMLElement, name: string) => {
    if (names.has(name)) container.classList.add('jarvis-clipboard-cut');
  };

  for (const row of root.querySelectorAll<HTMLElement>('.dx-filemanager-details .dx-data-row')) {
    if (row.classList.contains('dx-filemanager-parent-directory-item')) continue;
    const name = row.querySelector<HTMLElement>('.dx-filemanager-details-item-name')?.textContent?.trim() ?? '';
    mark(row, name);
  }
  for (const item of root.querySelectorAll<HTMLElement>('.dx-filemanager-thumbnails-item')) {
    const name = item.querySelector<HTMLElement>('.dx-filemanager-thumbnails-item-name')?.textContent?.trim() ?? '';
    mark(item, name);
  }
}

function entriesFromSelection(items: readonly FileSystemItem[], routes: readonly MountRoute[]): ClipboardEntry[] {
  const entries: ClipboardEntry[] = [];
  for (const item of items) {
    const parsed = parseItemPathForRoutes(item, routes);
    if (!parsed?.relative) continue;
    if (isMountUnavailable(parsed.mount)) {
      throw new Error(mountHealthMessage(parsed.mount) ?? `"${parsed.mount.key}" is unavailable.`);
    }
    entries.push({
      mountId: parsed.mount.id,
      mountKey: parsed.mount.key,
      relativePath: parsed.relative,
      name: item.name,
      isDirectory: Boolean(item.isDirectory),
      virtualPath: toVirtualPath(parsed.mount.key, parsed.relative)
    });
  }
  return entries;
}

function entryToFileSystemItem(entry: ClipboardEntry): FileSystemItem {
  return {
    name: entry.name,
    path: entry.virtualPath,
    key: entry.virtualPath,
    isDirectory: entry.isDirectory
  } as FileSystemItem;
}

export function cutOrCopySelection(
  items: readonly FileSystemItem[],
  routes: readonly MountRoute[],
  operation: ClipboardOperation
): boolean {
  let entries: ClipboardEntry[];
  try {
    entries = entriesFromSelection(items, routes);
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), 'warning', 4000);
    return false;
  }
  if (entries.length === 0) {
    showToast('Select one or more files or folders to cut or copy.', 'warning', 3000);
    return false;
  }

  if (operation === 'cut') {
    for (const entry of entries) {
      const mount = routes.find((route) => route.id === entry.mountId);
      if (mount?.mode !== 'ReadWrite') {
        showToast(`"${entry.name}" is in a read-only location and cannot be cut.`, 'warning', 4000);
        return false;
      }
    }
  }

  clipboard = { operation, items: entries };
  resetTransferBatchState();
  notifyClipboardChanged();

  const label = clipboardItemLabel(clipboard);
  const verb = operation === 'cut' ? 'Cut' : 'Copied';
  showSuccessToast(`${verb} ${label}. Open a folder and paste (Ctrl+V).`);
  return true;
}

export async function pasteClipboardIntoDirectory(
  destinationDirectory: FileSystemItem,
  routes: readonly MountRoute[]
): Promise<void> {
  if (!clipboard?.items.length) {
    showToast('Nothing to paste. Cut or copy items first.', 'info', 3000);
    return;
  }

  const transferOperation = clipboard.operation === 'cut' ? 'Move' : 'Copy';
  const pending = [...clipboard.items];
  const label = clipboardItemLabel(clipboard);
  const destinationName = destinationDirectory.name?.trim() || 'this folder';
  const failures: string[] = [];
  const failedEntries: ClipboardEntry[] = [];
  let successCount = 0;

  resetTransferBatchState();

  for (const entry of pending) {
    try {
      await performFileTransfer(
        entryToFileSystemItem(entry),
        destinationDirectory,
        routes,
        transferOperation
      );
      successCount += 1;
    } catch (error) {
      failures.push(`${entry.name}: ${error instanceof Error ? error.message : String(error)}`);
      failedEntries.push(entry);
    }
  }

  const wasCut = clipboard.operation === 'cut';
  if (wasCut && successCount > 0) {
    clipboard = failedEntries.length > 0 ? { operation: 'cut', items: failedEntries } : null;
    notifyClipboardChanged();
  }

  if (failures.length === 0) {
    showSuccessToast(`Pasted ${label} into ${destinationName}.`);
    return;
  }

  if (successCount > 0) {
    showToast(
      `Pasted ${successCount} item${successCount === 1 ? '' : 's'} into ${destinationName}; ${failures.length} failed.`,
      'warning',
      5000
    );
  } else {
    showToast(failures[0] ?? 'Paste failed.', 'error', 5000);
  }
}

export function canPasteIntoDirectory(
  destinationDirectory: FileSystemItem | null | undefined,
  routes: readonly MountRoute[],
  canWriteCurrentMount: boolean
): boolean {
  if (!hasClipboardItems() || !canWriteCurrentMount || !destinationDirectory) return false;
  const parsed = parseItemPathForRoutes(destinationDirectory, routes);
  if (!parsed || isMountUnavailable(parsed.mount)) return false;
  return parsed.mount.mode === 'ReadWrite';
}

export function isClipboardShortcutTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}
